import React, { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface DropzoneProps {
  onFiles: (files: File[]) => void | Promise<void>;
  accept?: string[];
  multiple?: boolean;
  maxSizeMB?: number;
  className?: string;
  ariaLabel?: string;
  onError?: (message: string) => void;
}

function isAccepted(file: File, accept?: string[]) {
  if (!accept || accept.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept.some((rule) => {
    const r = rule.toLowerCase();
    if (r.endsWith("/*")) {
      const base = r.replace("/*", "");
      return type.startsWith(base);
    }
    if (r.startsWith(".")) return name.endsWith(r);
    return type === r;
  });
}

export default function MeasureEvidenceFileUploader({
  onFiles,
  accept = [
    "image/*",
    ".csv",
    "application/pdf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
  multiple = true,
  maxSizeMB = 10,
  className,
  ariaLabel = "Upload files",
  onError,
}: DropzoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const arr = Array.from(fileList);
      const filtered: File[] = [];
      for (const f of arr) {
        if (!isAccepted(f, accept)) {
          onError?.(`Unsupported file: ${f.name}`);
          continue;
        }
        if (f.size > maxSizeMB * 1024 * 1024) {
          onError?.(`${f.name} exceeds ${maxSizeMB}MB limit`);
          continue;
        }
        filtered.push(f);
      }
      if (filtered.length) onFiles(filtered);
    },
    [accept, maxSizeMB, onFiles, onError]
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      handleFiles(e.dataTransfer.files);
    }
  };
  const onDragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData.items;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const f = it.getAsFile();
      if (f) files.push(f);
    }
    if (files.length) handleFiles(files);
  };

  const trigger = () => inputRef.current?.click();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.currentTarget.files;
    if (f && f.length) handleFiles(f);
    e.currentTarget.value = "";
  };

  return (
    <div
      role="button"
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={trigger}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && trigger()}
      onDrop={onDrop}
      onDragOver={onDragOverHandler}
      onDragLeave={onDragLeave}
      onPaste={onPaste}
      className={cn(
        "flex h-20 w-full cursor-pointer items-center justify-center rounded-md border border-dashed bg-muted/30 text-sm text-muted-foreground transition-colors",
        dragOver ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted/50",
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        multiple={multiple}
        accept={accept.join(",")}
        onChange={onChange}
      />
      <div className="px-3 text-center">
        <p>
          <span className="font-medium">Drag & drop</span> or paste files here, or click to browse
        </p>
        <p className="text-xs">Images, PDF, CSV, XLS/XLSX • up to {maxSizeMB}MB each</p>
      </div>
    </div>
  );
}



